/**
 * AI Feature Access Control Service
 * 
 * Validates user's plan allows access to AI features based on plan_features table.
 * Uses the following feature keys from the database:
 * - ai_limit: Controls AI subtask generation and task analysis (limit_value = monthly limit, -1 = unlimited)
 * - integration_gemini: Gemini AI integration access
 * - integration_openrouter: OpenRouter AI integration access
 */

import { config } from './config';

const API_BASE = config.apiBaseUrl;

export interface AIFeatureAccess {
  allowed: boolean;
  reason?: string;
  upgradeRequired?: boolean;
  currentUsage?: number;
  limit?: number | string;
}

/**
 * Check if user has access to a specific AI feature
 * @param featureKey - The feature to check (ai_limit, integration_gemini, integration_openrouter)
 */
export const checkAIFeatureAccess = async (featureKey: string): Promise<AIFeatureAccess> => {
  try {
    // Get CSRF token from cookie
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
    };

    const csrfToken = getCookie('csrf_token');

    const response = await fetch(`${API_BASE}/user/ai-feature-access?feature=${encodeURIComponent(featureKey)}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 401) {
        return {
          allowed: false,
          reason: 'Session expired. Please log in again.',
          upgradeRequired: false
        };
      }
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to check AI feature access:', error);
    // Default to allowed if check fails (graceful degradation for API issues)
    return { allowed: true };
  }
};

/**
 * Check if user can use AI features (subtask generation, task analysis)
 * Maps to plan_features: ai_limit
 */
export const canUseAIFeatures = async (): Promise<AIFeatureAccess> => {
  return checkAIFeatureAccess('ai_limit');
};

/**
 * Check if user can use AI subtask generation
 */
export const canUseAISubtaskGeneration = async (): Promise<AIFeatureAccess> => {
  return checkAIFeatureAccess('ai_subtask_generation');
};

/**
 * Check if user can use AI task analysis
 */
export const canUseAITaskAnalysis = async (): Promise<AIFeatureAccess> => {
  return checkAIFeatureAccess('ai_task_analysis');
};

/**
 * Check if a specific AI integration is enabled for user's plan
 * @param integrationId - Integration ID (e.g., 'gemini', 'openrouter')
 */
export const checkIntegrationAccess = async (integrationId: string): Promise<AIFeatureAccess> => {
  const featureKey = `integration_${integrationId}`;
  return checkAIFeatureAccess(featureKey);
};

/**
 * Get the user's AI credit usage summary (current vs limit)
 */
export const getAICreditUsage = async (): Promise<AIFeatureAccess> => {
  return checkAIFeatureAccess('ai_limit');
};

/**
 * Log AI usage for tracking (called after successful AI operation)
 * @param action - The AI action performed (e.g., 'subtask_generation', 'task_analysis')
 * @param taskId - Optional task ID for context
 */
export const logAIUsage = async (action: string, taskId?: string): Promise<void> => {
  try {
    // Get CSRF token from cookie
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
    };

    const csrfToken = getCookie('csrf_token');

    await fetch(`${API_BASE}/user/ai-usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ action, taskId })
    });
  } catch (error) {
    console.warn('Failed to log AI usage:', error);
    // Don't throw - usage logging is non-critical
  }
};

/**
 * Show upgrade prompt if AI feature is not available
 * @param accessResult - Result from checkAIFeatureAccess
 * @returns true if user chose to upgrade, false otherwise
 */
export const promptUpgradeIfNeeded = (accessResult: AIFeatureAccess): boolean => {
  if (accessResult.allowed) return false;

  const message = accessResult.reason || 'This AI feature is not available in your current plan.';
  const shouldUpgrade = window.confirm(
    `${message}\n\nWould you like to upgrade your plan to unlock AI features?`
  );

  if (shouldUpgrade) {
    window.location.hash = '#/pricing';
  }

  return shouldUpgrade;
};
